import { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  TextField,
  InputAdornment,
  Chip,
  Divider, 
  IconButton,
  useMediaQuery,
  useTheme,
  Skeleton,
  CircularProgress,
  Avatar,
  Pagination,
  PaginationItem,
  Stack
} from "@mui/material";
import { keyframes } from '@mui/system';
import { Link, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import backdrop from "../img/backdrop.png";
import screamifyLogo from "../img/screamifyweblogo.png";
import Header from "../components/Header";
import Footer from "../components/Footer";

// Airtable API info
const AIRTABLE_API_KEY = 'pat4FML0uvc5EtZiq.4b9c4db63db5d87249782d8456c6341da6a25cc41041487340138cd1d4453a99';
const AIRTABLE_BASE_ID = 'appttt0VypJe8DyRL';
const AIRTABLE_TABLE_NAME = 'Posts';

// Pagination settings
const POSTS_PER_PAGE = 15;

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Header text styles that match the example
const headerStyle = {
  fontFamily: 'serif',
  fontWeight: 800,
  color: '#ffffff',
  textTransform: 'uppercase',
};

// Subtitle style for categories (REVIEWS, MOVIES, etc.)
const categoryStyle = {
  fontFamily: 'sans-serif',
  fontWeight: 700,
  color: '#666666',
  textTransform: 'uppercase',
  fontSize: '0.9rem',
  letterSpacing: '1px',
};

function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  
  // Update URL when page changes
  useEffect(() => {
    if (page > 1) {
      setSearchParams({ page: page.toString() });
    } else {
      setSearchParams({});
    }
  }, [page, setSearchParams]);
  
  // Fetch posts from Airtable
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${AIRTABLE_TABLE_NAME}?view=Grid%20view&sort%5B0%5D%5Bfield%5D=Date&sort%5B0%5D%5Bdirection%5D=desc`,
          {
            headers: {
              'Authorization': `Bearer ${AIRTABLE_API_KEY}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (!response.ok) {
          throw new Error(`Airtable API returned ${response.status}`);
        }
        
        const data = await response.json();
        setPosts(data.records);
      } catch (err) {
        console.error('Error fetching posts:', err);
        setError('Failed to load blog posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPosts();
  }, []);
  
  // Filter posts based on search term
  const filteredPosts = posts.filter(post => {
    if (!post || !post.fields) return false;
    
    const title = post.fields.Title || '';
    const content = post.fields.Content || '';
    const category = post.fields.Category || '';
    return title.toLowerCase().includes(searchTerm.toLowerCase()) || 
           content.toLowerCase().includes(searchTerm.toLowerCase()) ||
           category.toLowerCase().includes(searchTerm.toLowerCase());
  });
  
  // Pagination logic
  const totalPosts = filteredPosts.length;
  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);
  
  // Get current posts for the current page
  const currentPosts = filteredPosts.slice(
    (page - 1) * POSTS_PER_PAGE, 
    page * POSTS_PER_PAGE
  );
  
  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Keep page in bounds when filtering
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [totalPages, page]);
  
  // Truncate text to a specific length
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };
  
  // Format date to be more readable
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };
  
  // Format date to match example (e.g., "32 minutes ago")
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };
  
  return (
    <Box 
      sx={{ 
        width: '100%', 
        minHeight: '100vh',
        backgroundImage: `url(${backdrop})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          zIndex: 0,
        }
      }}
    >
      <HelmetProvider>
        <Helmet>
          <title>Horror Blog | Screamify - Independent Horror News, Reviews & Insights{page > 1 ? ` - Page ${page}` : ''}</title>
          <meta name="description" content="Dive into the world of indie horror cinema with expert reviews, filmmaker interviews, festival news, and horror industry analysis. Your ultimate guide to all things horror." />
          <meta name="keywords" content="horror blog, indie horror films, horror movie reviews, horror film festivals, horror directors, horror streaming, slasher films, supernatural horror, psychological horror, horror anthology, horror news, horror insights, indie filmmakers, horror analysis, horror community" />
          <meta property="og:title" content="Horror Blog | Screamify - Independent Horror News, Reviews & Insights{page > 1 ? ` - Page ${page}` : ''}" />
          <meta property="og:description" content="Dive into the world of indie horror cinema with expert reviews, filmmaker interviews, and in-depth horror industry analysis. Your ultimate guide to horror films." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`https://screamify.com/blog${page > 1 ? `?page=${page}` : ''}`} />
          <meta property="og:image" content="https://screamify.com/social-share.png" />
          <meta property="og:image:alt" content="Screamify Horror Blog" />
          <meta property="og:site_name" content="Screamify" />
          <meta property="og:locale" content="en_US" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Horror Blog | Screamify - Horror Film Insights{page > 1 ? ` - Page ${page}` : ''}" />
          <meta name="twitter:description" content="Expert reviews, filmmaker interviews, and in-depth analysis of indie horror cinema and industry trends." />
          <meta name="twitter:image" content="https://screamify.com/social-share.png" />
          <meta name="twitter:site" content="@screamify" />
          <meta name="robots" content="index, follow, max-image-preview:large" />
          <meta name="author" content="Screamify Team" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <link rel="canonical" href={`https://screamify.com/blog${page > 1 ? `?page=${page}` : ''}`} />
          {page > 1 && <link rel="prev" href={`https://screamify.com/blog${page > 2 ? `?page=${page-1}` : ''}`} />}
          {page < totalPages && <link rel="next" href={`https://screamify.com/blog?page=${page+1}`} />}
          
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Blog",
              "name": "Screamify Horror Blog",
              "description": "In-depth analysis, reviews, and news about independent horror cinema, horror film festivals, and horror industry trends",
              "url": "https://screamify.com/blog",
              "publisher": {
                "@type": "Organization",
                "name": "Screamify",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://screamify.com/Favicon-01.png"
                }
              },
              "blogPost": posts && posts.length > 0 ? posts.filter(post => post && post.fields).map(post => ({
                "@type": "BlogPosting",
                "headline": post.fields.Title || "Horror Blog Post",
                "datePublished": post.fields.Date || new Date().toISOString(),
                "dateModified": post.fields.UpdatedDate || post.fields.Date || new Date().toISOString(),
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `https://screamify.com/blog/${post.fields.BlogId || ''}`
                },
                "author": {
                  "@type": "Person",
                  "name": post.fields.Author || "Screamify Team"
                },
                "image": post.fields.Pic1Url || "https://screamify.com/social-share.png",
                "url": `https://screamify.com/blog/${post.fields.BlogId || ''}`,
                "description": truncateText(post.fields.Content || '', 160) || "Horror insights and analysis"
              })) : [],
              "keywords": "horror blog, horror movie reviews, indie horror films, horror film analysis, horror industry trends, horror film festivals, horror directors, supernatural horror, psychological horror",
              "inLanguage": "en-US"
            })}
          </script>
          
          {/* BreadcrumbList Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://screamify.com/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://screamify.com/blog"
                },
                ...(page > 1 ? [{
                  "@type": "ListItem",
                  "position": 3,
                  "name": `Page ${page}`,
                  "item": `https://screamify.com/blog?page=${page}`
                }] : [])
              ]
            })}
          </script>
          
          {/* Website Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "Screamify",
              "url": "https://screamify.com",
              "potentialAction": {
                "@type": "SearchAction",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": "https://screamify.com/blog?search={search_term_string}"
                },
                "query-input": "required name=search_term_string"
              }
            })}
          </script>
        </Helmet>
      </HelmetProvider>
      
      <Header />
      
      <Box sx={{ height: '64px' }} /> {/* Spacer for header */}
      
      <Container 
        maxWidth="xl" 
        sx={{ 
          position: 'relative', 
          zIndex: 1, 
          flex: 1, 
          py: 4,
          px: { xs: 2, sm: 3, md: 4 } // Adjusted padding for better mobile display
        }}
      >
        {/* Blog Header */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: { xs: 4, sm: 6 }, // Reduced bottom margin on mobile
          borderBottom: '2px solid #ff0000',
          pb: 2,
        }}>
          <Typography 
            variant="h3" 
            sx={{ 
              ...headerStyle,
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' }, // Smaller font on mobile
              textShadow: '0 0 15px rgba(255, 0, 0, 0.4)',
              mb: 2,
              color: '#ff0000',
              animation: `${fadeIn} 1s ease-out`
            }}
          >
            LATEST HEADLINES
            {page > 1 && <Typography component="span" sx={{ fontSize: '0.6em', ml: 2, fontWeight: 400 }}>Page {page}</Typography>}
          </Typography>
        </Box>
        
        {/* Search Bar */}
        <Box sx={{ maxWidth: '600px', mx: 'auto', mb: { xs: 4, sm: 6 } }}> {/* Reduced margin on mobile */}
          <TextField
            fullWidth
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1); // Reset to first page on search
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                color: 'white',
                height: { xs: '48px', sm: 'auto' }, // Taller input field on mobile for easier touch
                '& fieldset': {
                  borderColor: 'rgba(255, 0, 0, 0.3)',
                  borderWidth: '2px',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 0, 0, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#ff0000',
                },
              },
              '& .MuiInputBase-input': {
                fontSize: { xs: '16px', sm: 'inherit' }, // 16px prevents iOS zoom on focus
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255, 255, 255, 0.7)',
                opacity: 1,
              },
            }}
          />
        </Box>
        
        {loading ? (
          // Loading state
          <Box sx={{ py: 4 }}>
            {/* Skeleton for featured post */}
            <Box sx={{ mb: 6 }}>
              <Skeleton 
                variant="rectangular" 
                width="100%" 
                height={400} 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: '0px', mb: 2 }} 
              />
              <Skeleton 
                variant="text" 
                width="70%" 
                height={60} 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
              />
              <Skeleton 
                variant="text" 
                width="100%" 
                height={30} 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
              />
              <Skeleton 
                variant="text" 
                width="40%" 
                height={30} 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
              />
            </Box>
            
            {/* Skeletons for remaining posts */}
            <Grid container spacing={4}>
              {[1, 2, 3].map((item) => (
                <Grid item xs={12} key={item}>
                  <Skeleton 
                    variant="rectangular" 
                    width="30%" 
                    height={200} 
                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: '0px', mb: 2, display: 'inline-block' }} 
                  />
                  <Box sx={{ display: 'inline-block', width: '70%', pl: 2, verticalAlign: 'top' }}>
                    <Skeleton 
                      variant="text" 
                      width="80%" 
                      height={40} 
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
                    />
                    <Skeleton 
                      variant="text" 
                      width="100%" 
                      height={20} 
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
                    />
                    <Skeleton 
                      variant="text" 
                      width="50%" 
                      height={20} 
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
                    />
                  </Box>
                  <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : error ? (
          // Error state
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 10, 
              px: 2,
              backgroundColor: 'rgba(255, 0, 0, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 0, 0, 0.3)'
            }}
          >
            <Typography variant="h5" sx={{ color: '#ff5555', mb: 2 }}>
              {error}
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Please check your connection and try refreshing the page.
            </Typography>
          </Box>
        ) : filteredPosts.length === 0 ? (
          // No results from search
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 10,
              px: 2 
            }}
          >
            <Typography variant="h5" sx={{ color: 'white', mb: 2 }}>
              No posts found matching "{searchTerm}"
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Try a different search term or browse our latest articles.
            </Typography>
          </Box>
        ) : (
          // Content loaded successfully
          <Box>
            {/* Blog Posts Grid - List Style like the example */}
            {currentPosts.map((post, index) => (
              <Box 
                key={post.id}
                sx={{ 
                  mb: { xs: 5, sm: 4 }, // More margin on mobile for better separation
                  animation: `${fadeIn} 1s ease-out ${0.1 * index}s both`,
                  borderBottom: index < currentPosts.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                  pb: { xs: 5, sm: 4 }, // More padding on mobile
                }}
              >
                <Grid container spacing={{ xs: 2, sm: 3 }}> {/* Tighter spacing on mobile */}
                  <Grid item xs={12} md={4}>
                    <Box 
                      component={Link}
                      to={`/blog/${post.fields.BlogId}`}
                      sx={{
                        display: 'block',
                        width: '100%',
                        height: 0,
                        paddingBottom: { xs: '66.67%', sm: '56.25%' }, // 3:2 ratio on mobile, 16:9 on desktop
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        borderRadius: { xs: '4px', sm: 0 }, // Slight rounding on mobile
                        mb: { xs: 2, md: 0 }, // Add bottom margin on mobile/tablet
                        '&:hover': {
                          transform: 'scale(1.02)',
                        },
                        '&:active': { // Better touch feedback
                          transform: 'scale(0.98)',
                          opacity: 0.9
                        }
                      }}
                    >
                      <Box
                        component="img"
                        src={post.fields.Pic1Url}
                        alt={post.fields.Title}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center',
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Box>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 1,
                        flexWrap: { xs: 'wrap', sm: 'nowrap' } // Wrap on mobile
                      }}>
                        <Typography 
                          component="span" 
                          sx={{
                            ...categoryStyle,
                            mr: 1.5,
                          }}
                        >
                          {post.fields.Category || 'UNCATEGORIZED'}
                        </Typography>
                        <Typography 
                          component="span" 
                          sx={{ 
                            color: 'rgba(255, 255, 255, 0.6)', 
                            fontSize: '0.9rem',
                            fontWeight: 400,
                          }}
                        >
                          / {formatTimeAgo(post.fields.Date)}
                        </Typography>
                      </Box>
                      
                      <Typography
                        variant="h1"
                        component={Link}
                        to={`/blog/${post.fields.BlogId}`}
                        sx={{
                          fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.7rem' }, // Smaller font on mobile
                          fontWeight: 600,
                          color: 'white',
                          textDecoration: 'none',
                          lineHeight: 1.3,
                          display: 'block',
                          mb: { xs: 2, sm: 1 }, // More margin on mobile
                          '&:hover': {
                            color: '#ff0000',
                          },
                          '&:active': { // Better touch feedback
                            color: '#cc0000',
                          }
                        }}
                      >
                        {post.fields.Title}
                      </Typography>
                      
                      <Box sx={{ display: { xs: 'block', sm: 'none', md: 'block' } }}> {/* Show on mobile and desktop, hide on tablet */}
                        <Typography
                          sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: { xs: '0.9rem', md: '1rem' }, // Smaller font on mobile
                            lineHeight: 1.5,
                          }}
                        >
                          {truncateText(post.fields.Content, isMobile ? 100 : 150)} {/* Shorter excerpt on mobile */}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ))}
            
            {/* Pagination - Always show regardless of page count */}
            <Stack spacing={2} sx={{ mt: 6, mb: { xs: 8, sm: 6 }, display: 'flex', alignItems: 'center' }}> {/* More margin at bottom on mobile */}
              <Pagination 
                count={Math.max(1, totalPages)} 
                page={page}
                onChange={handlePageChange}
                size={isMobile ? "medium" : "medium"} // Keep medium size for better touch targets
                showFirstButton={!isMobile}
                showLastButton={!isMobile}
                siblingCount={isMobile ? 0 : 1}
                disabled={totalPages <= 1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    minWidth: { xs: '36px', sm: '32px' }, // Larger touch targets on mobile
                    height: { xs: '36px', sm: '32px' },
                    margin: { xs: '0 2px', sm: '0 4px' },
                  }
                }}
                renderItem={(item) => (
                  <PaginationItem
                    {...item}
                    sx={{
                      color: 'white',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 0, 0, 0.8)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 0, 0, 0.6)',
                        }
                      },
                      '&.MuiPaginationItem-ellipsis': {
                        color: 'rgba(255, 255, 255, 0.6)',
                      },
                      '&.MuiPaginationItem-previousNext': {
                        color: 'rgba(255, 255, 255, 0.8)',
                      },
                      '&.MuiPaginationItem-firstLast': {
                        color: 'rgba(255, 255, 255, 0.8)',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  />
                )}
              />
              <Typography variant="body2" sx={{ 
                color: 'rgba(255, 255, 255, 0.6)', 
                textAlign: 'center',
                fontSize: { xs: '0.8rem', sm: '0.875rem' } // Smaller font on mobile
              }}>
                Page {page} of {Math.max(1, totalPages)} · Showing {currentPosts.length} of {totalPosts} posts
              </Typography>
            </Stack>
          </Box>
        )}
      </Container>
      
      <Footer />
    </Box>
  );
}

export default BlogPage; 